import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  Search20Regular,
  Person24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getTenantById,
  getTenants,
  type Tenant as ApiTenant,
} from "../../inquilinos/inquilinosApi";

interface InquilinoView {
  id: number;
  nome: string;
  nif: string;
  email: string;
  telefone: string;
  iban?: string | null;
  fiador?: string | null;
}

const formatOptional = (value?: string | null): string =>
  value && value.trim() ? value : "—";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar inquilinos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os inquilinos.";
};

const mapTenantToView = (tenant: ApiTenant): InquilinoView => {
  return {
    id: tenant.id,
    nome: tenant.name,
    nif: tenant.taxId ?? "",
    email: tenant.email ?? "",
    telefone: tenant.phoneNumber ?? "",
    iban: tenant.iban ?? null,
    fiador: tenant.guarantor ?? null,
  };
};

export function InquilinosView() {
  const navigate = useNavigate();
  const [inquilinos, setInquilinos] = useState<InquilinoView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadTenants = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getTenants();
        if (!isMounted) return;

        const mapped = response.map(mapTenantToView);
        setInquilinos(mapped);
        setSelectedTenantId((current) => {
          if (current && mapped.some((item) => item.id === current)) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setInquilinos([]);
        setSelectedTenantId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTenants();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredInquilinos = useMemo(
    () =>
      inquilinos.filter((inquilino) => {
        const matchesSearch =
          inquilino.nome
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          inquilino.nif.includes(searchQuery);
        return matchesSearch;
      }),
    [inquilinos, searchQuery],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredInquilinos.length === 0) {
      setSelectedTenantId(null);
      return;
    }

    const hasSelection = filteredInquilinos.some(
      (entry) => entry.id === selectedTenantId,
    );
    if (!hasSelection) {
      setSelectedTenantId(filteredInquilinos[0].id);
    }
  }, [filteredInquilinos, isLoading, selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) return;

    let isMounted = true;

    const loadSelectedTenant = async () => {
      try {
        const response = await getTenantById(selectedTenantId);
        if (!isMounted) return;

        const mapped = mapTenantToView(response);
        setInquilinos((current) =>
          current.map((inquilino) =>
            inquilino.id === selectedTenantId ? mapped : inquilino,
          ),
        );
      } catch {
        // Keep list data if detail fetch fails.
      }
    };

    loadSelectedTenant();

    return () => {
      isMounted = false;
    };
  }, [selectedTenantId]);

  const selectedInquilino = filteredInquilinos.find(
    (i) => i.id === selectedTenantId,
  );

  const getInitials = (nome: string) => {
    return nome
      .split(" ")
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const handleTenantSelect = (tenantId: number) => {
    setSelectedTenantId(tenantId);
    setIsMobileDetailOpen(true);
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/inquilinos/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Inquilino
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Inquilinos"
        subtitle="Lista de inquilinos e dados de arrendamento"
        actions={actions}
      />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar inquilinos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar inquilinos..."
                : `${filteredInquilinos.length} inquilino${
                    filteredInquilinos.length !== 1 ? "s" : ""
                  } encontrado${
                    filteredInquilinos.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar inquilinos...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Person24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredInquilinos.map((inquilino) => (
                <button
                  key={inquilino.id}
                  onClick={() => handleTenantSelect(inquilino.id)}
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedTenantId === inquilino.id ? "bg-accent/50" : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedTenantId === inquilino.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedTenantId !== inquilino.id) {
                      e.currentTarget.style.backgroundColor = "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedTenantId !== inquilino.id) {
                      e.currentTarget.style.backgroundColor = "var(--card)";
                    }
                  }}
                >
                  {selectedTenantId === inquilino.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    <div
                      className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                        color: "var(--primary)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {getInitials(inquilino.nome)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate"
                        style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        {inquilino.nome}
                      </p>

                      <p
                        className="text-secondary-text mt-0.5"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        NIF: {formatOptional(inquilino.nif)}
                      </p>

                      <p
                        className="text-secondary-text mt-1"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(inquilino.email)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading && !loadError && filteredInquilinos.length === 0 && (
              <div className="p-8 text-center text-secondary-text">
                <p>Nenhum inquilino encontrado</p>
              </div>
            )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedInquilino ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <Person24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um inquilino para ver os detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0"
                      style={{
                        fontSize: "var(--text-large)",
                        fontWeight: "var(--font-weight-semi-bold)",
                        color: "var(--primary)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {getInitials(selectedInquilino.nome)}
                    </div>

                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight: "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedInquilino.nome}
                      </h3>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{ borderRadius: "var(--radius-button)" }}
                      onClick={() =>
                        navigate(`/inquilinos/editar/${selectedInquilino.id}`)
                      }
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Inquilino</span>
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Dados do Inquilino
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      NIF
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedInquilino.nif)}</p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Email
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedInquilino.email)}</p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Telefone
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedInquilino.telefone)}</p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      IBAN
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedInquilino.iban)}</p>
                  </div>

                  <div className="sm:col-span-2">
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Fiador
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedInquilino.fiador)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
